import { formatPageTitle } from "./pageTitle.js";

export const SITE_URL = "https://neattick.com";
export const OG_IMAGE_PATH = "/marketing/og.png";

export type MarketingPage = {
  path: string;
  outFile: string;
  title: string;
  description: string;
  ogType?: "website" | "article";
};

export const MARKETING_PAGES: MarketingPage[] = [
  {
    path: "/",
    outFile: "landing/index.html",
    title: "NeatTick — Reusable shared checklists for trips, events, and repeat work",
    description:
      "Free web app for reusable, shareable checklists. Build a list once, turn it into a template, and check it off together — packing, events, and repeat processes.",
  },
  {
    path: "/how-it-works",
    outFile: "how-it-works/index.html",
    title: "How NeatTick works",
    description:
      "Lists, templates, and checklists — start a checklist in about a minute, save it as a template, and share it so checkmarks sync live.",
  },
  {
    path: "/contact",
    outFile: "contact/index.html",
    title: "Contact NeatTick",
    description: "Send a message, report a bug, or request a feature. Every submission gets a ticket reference.",
  },
  {
    path: "/privacy",
    outFile: "privacy/index.html",
    title: "NeatTick privacy",
    description: "What NeatTick collects, how long we keep it, and how to ask us to delete it.",
  },
  {
    path: "/packing-list",
    outFile: "packing-list/index.html",
    title: "Shared packing list — NeatTick",
    description:
      "A reusable packing list you share with family or friends. Check items off live so nobody packs the sunscreen twice.",
  },
  {
    path: "/shared-checklist",
    outFile: "shared-checklist/index.html",
    title: "Shared checklist with live checkmarks — NeatTick",
    description:
      "Share a checklist with a link. When two or more people are on it, checkmarks sync live and you can see who checked what.",
  },
  {
    path: "/wedding-checklist",
    outFile: "wedding-checklist/index.html",
    title: "Wedding day-of checklist — NeatTick",
    description:
      "A shareable wedding-day run sheet. Keep the couple and the crew on the same list without a group text.",
  },
  {
    path: "/camping-checklist",
    outFile: "camping-checklist/index.html",
    title: "Camping packing checklist — NeatTick",
    description:
      "A reusable camping packing list you share with the group. Tent, stove, and the cable everyone forgets — checked off live.",
  },
  {
    path: "/onboarding-checklist",
    outFile: "onboarding-checklist/index.html",
    title: "New hire onboarding checklist — NeatTick",
    description:
      "A reusable onboarding checklist you instantiate per hire and share so the new person can see what is done.",
  },
];

export const PRIVATE_PATH_PREFIXES = [
  "/login",
  "/register",
  "/verify",
  "/forgot",
  "/auth/",
  "/lists",
  "/templates",
  "/checklists",
  "/profile",
  "/admin",
  "/s/",
  "/invite/",
  "/request-access/",
  "/share-decision/",
] as const;

export function isPrivatePath(pathname: string): boolean {
  return PRIVATE_PATH_PREFIXES.some((p) => {
    if (p.endsWith("/")) return pathname.startsWith(p);
    return pathname === p || pathname.startsWith(`${p}/`);
  });
}

export function isIndexableCommunityName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) return false;
  if (/\s\d{4,}$/.test(trimmed)) return false;
  if (/\bverify\b/i.test(trimmed)) return false;
  if (/demo verify purpose/i.test(trimmed)) return false;
  return true;
}

export function communityListTitle(list: { name: string; kind: string }): string {
  const noun = list.kind === "todo" ? "checklist" : "packing list";
  return formatPageTitle(`${list.name} ${noun}`);
}

export function communityListDescription(list: {
  name: string;
  kind: string;
  description?: string;
  itemCount: number;
}): string {
  if (list.description && list.description.trim()) return list.description.trim();
  const noun = list.kind === "todo" ? "checklist" : "packing list";
  return `A reusable ${noun} on NeatTick with ${list.itemCount} items. Copy it into your workspace.`;
}

export function communityTemplateTitle(template: { name: string }): string {
  return formatPageTitle(`${template.name} checklist template`);
}

export function communityTemplateDescription(template: {
  description?: string;
  sectionNames: string[];
}): string {
  const description = template.description?.trim();
  if (description) return description;
  return `A ready-made checklist template: ${template.sectionNames.join(", ")}`;
}

export function robotsTxt(siteUrl: string): string {
  const origin = siteUrl.replace(/\/+$/, "");
  return [
    "User-agent: *",
    "Allow: /",
    "Disallow: /login",
    "Disallow: /register",
    "Disallow: /verify",
    "Disallow: /forgot",
    "Disallow: /auth/",
    "Disallow: /lists",
    "Disallow: /templates",
    "Disallow: /checklists",
    "Disallow: /profile",
    "Disallow: /admin",
    "Disallow: /s/",
    "Disallow: /invite/",
    "Disallow: /request-access/",
    "Disallow: /share-decision/",
    "",
    "User-agent: GPTBot",
    "Allow: /",
    "",
    "User-agent: ClaudeBot",
    "Allow: /",
    "",
    "User-agent: PerplexityBot",
    "Allow: /",
    "",
    "User-agent: Google-Extended",
    "Allow: /",
    "",
    "User-agent: Applebot-Extended",
    "Allow: /",
    "",
    `Sitemap: ${origin}/sitemap.xml`,
    "",
  ].join("\n");
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function sitemapXml(
  urls: { loc: string; changefreq?: string; priority?: string }[]
): string {
  const entries = urls
    .map((u) => {
      const parts = [`    <loc>${escapeXml(u.loc)}</loc>`];
      if (u.changefreq) parts.push(`    <changefreq>${escapeXml(u.changefreq)}</changefreq>`);
      if (u.priority) parts.push(`    <priority>${escapeXml(u.priority)}</priority>`);
      return `  <url>\n${parts.join("\n")}\n  </url>`;
    })
    .join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${entries}\n</urlset>\n`;
}

export const FAQ_ITEMS: { q: string; a: string }[] = [
  { q: "Is it free?", a: "Yes. Free to use, no credit card." },
  {
    q: "What's the difference between a list, a template, and a checklist?",
    a: "A checklist is the thing you check off. A template is a reusable blueprint for making that checklist again. A list is a building block you can drop into templates. Most people start with a checklist and graduate into the rest.",
  },
  {
    q: "Do the people I share with need an account?",
    a: "Yes, so we can show who checked what — but signup takes seconds.",
  },
  { q: "Does it work on my phone?", a: "It's built for phones first. That's where you actually check things off." },
  { q: "Can I use it for work things, not just packing?", a: "Yes — any process you repeat." },
  {
    q: "What happens to my checklist if I edit the original list?",
    a: "Nothing. Each checklist is a snapshot. Editing the source lists or template never changes checklists you've already started.",
  },
];

export function jsonLdGraph(args: {
  pageUrl: string;
  title: string;
  description: string;
  includeSoftware?: boolean;
  includeFaq?: boolean;
  itemList?: { name: string; description?: string; items: string[] };
}): object {
  const graph: Record<string, unknown>[] = [
    {
      "@type": "WebSite",
      name: "NeatTick",
      url: SITE_URL,
      description: args.description,
    },
    {
      "@type": "WebPage",
      name: args.title,
      description: args.description,
      url: args.pageUrl,
      isPartOf: { "@type": "WebSite", name: "NeatTick", url: SITE_URL },
    },
  ];

  if (args.includeSoftware) {
    graph.push({
      "@type": "SoftwareApplication",
      name: "NeatTick",
      url: SITE_URL,
      applicationCategory: "ProductivityApplication",
      operatingSystem: "Web browser",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description: args.description,
      image: `${SITE_URL}${OG_IMAGE_PATH}`,
    });
  }

  if (args.includeFaq) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
  }

  if (args.itemList) {
    graph.push({
      "@type": "ItemList",
      name: args.itemList.name,
      description: args.itemList.description,
      itemListElement: args.itemList.items.map((name, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function canonicalUrl(siteUrl: string, path: string): string {
  const origin = siteUrl.replace(/\/+$/, "");
  return path === "/" ? `${origin}/` : `${origin}${path}`;
}

export type InjectMetaInput = {
  title: string;
  description: string;
  path: string;
  jsonLd?: object;
  noindex?: boolean;
};

export function injectMeta(shellHtml: string, input: InjectMetaInput, siteUrl: string): string {
  const url = canonicalUrl(siteUrl, input.path);
  const safeTitle = escapeHtml(input.title);
  const safeDesc = escapeHtml(input.description);
  const safeUrl = escapeHtml(url);
  const safeImage = escapeHtml(`${siteUrl.replace(/\/+$/, "")}${OG_IMAGE_PATH}`);
  const robots = input.noindex ? "noindex, nofollow" : "index,follow";

  let html = shellHtml;

  if (/<title>[^<]*<\/title>/i.test(html)) {
    html = html.replace(/<title>[^<]*<\/title>/i, `<title>${safeTitle}</title>`);
  } else {
    html = html.replace(/<\/head>/i, `  <title>${safeTitle}</title>\n  </head>`);
  }

  const stripPatterns = [
    /<meta\s+name=["']description["'][^>]*>\s*/gi,
    /<meta\s+name=["']robots["'][^>]*>\s*/gi,
    /<meta\s+property=["']og:[^"']+["'][^>]*>\s*/gi,
    /<meta\s+name=["']twitter:[^"']+["'][^>]*>\s*/gi,
    /<link\s+rel=["']canonical["'][^>]*>\s*/gi,
    /<script\s+type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>\s*/gi,
  ];
  for (const re of stripPatterns) html = html.replace(re, "");

  const tags = [
    `<meta name="description" content="${safeDesc}" />`,
    `<meta name="robots" content="${robots}" />`,
    `<link rel="canonical" href="${safeUrl}" />`,
    `<meta property="og:title" content="${safeTitle}" />`,
    `<meta property="og:description" content="${safeDesc}" />`,
    `<meta property="og:image" content="${safeImage}" />`,
    `<meta property="og:url" content="${safeUrl}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="NeatTick" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${safeTitle}" />`,
    `<meta name="twitter:description" content="${safeDesc}" />`,
    `<meta name="twitter:image" content="${safeImage}" />`,
  ];

  if (input.jsonLd) {
    const json = JSON.stringify(input.jsonLd).replace(/</g, "\\u003c");
    tags.push(`<script type="application/ld+json">${json}</script>`);
  }

  html = html.replace(/<\/head>/i, `    ${tags.join("\n    ")}\n  </head>`);
  return html;
}
